/**
 * PaymentMethodSelect - Seletor unificado de meios de pagamento
 *
 * Componente centralizado para listar meios de pagamento do tenant,
 * com ícones personalizados e separação visual entre ativos e inativos.
 *
 * Padroniza a UX em todo o sistema financeiro (modais de cobrança,
 * assinaturas, baixa manual, etc).
 *
 * @author Módulo Financeiro
 * RELEASE: 2026-06-09 v1.0.0 - Criação do componente unificado
 */
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFinanceiroMeiosPagamento } from '@/hooks/useFinanceiroMeiosPagamento';
import {
  PaymentMethodIcon,
  DefaultPaymentIcon,
} from '@/components/icons/PaymentMethodIcons';
import { cn } from '@/lib/utils';

/** Props do componente */
interface PaymentMethodSelectProps {
  /** Valor selecionado (id do meio) */
  value: string;
  /** Callback ao trocar a seleção */
  onValueChange: (value: string) => void;
  /** Placeholder do trigger */
  placeholder?: string;
  /** Desabilita o select */
  disabled?: boolean;
  /** Inclui meios inativos na lista (default: true) */
  includeInactive?: boolean;
  /** Classes adicionais para o trigger */
  className?: string;
  /** v1.1.0 — Quando definido, adiciona um item "Todos" no topo (usado como filtro) */
  allOptionLabel?: string;
  /** v1.1.0 — Valor do item "Todos" (default 'all') */
  allOptionValue?: string;
  /**
   * v1.2.0 (2026-09-12) — Filtra meios vinculados a esta conta corrente OU
   * globais (conta_corrente_id NULL). Quando omitido, lista todos.
   * Mesma regra aplicada nos modais de criação/edição de cobrança.
   */
  contaCorrenteId?: string | null;
}

/**
 * Select padronizado de meios de pagamento
 * - Lista ativos primeiro
 * - Inativos agrupados separadamente, com opacidade reduzida e badge
 * - Cada item exibe ícone personalizado conforme o tipo
 * - v1.1.0: suporta item "Todos" opcional (uso em filtros)
 */
export function PaymentMethodSelect({
  value,
  onValueChange,
  placeholder = 'Selecione',
  disabled,
  includeInactive = true,
  className,
  allOptionLabel,
  allOptionValue = 'all',
  contaCorrenteId,
}: PaymentMethodSelectProps) {
  const { meios = [], isLoading } = useFinanceiroMeiosPagamento();

  // v1.2.0 — Filtro por conta corrente: meios vinculados à conta OU globais
  // (conta_corrente_id NULL). Meios de outras contas não se misturam.
  const meiosFiltrados = contaCorrenteId
    ? meios.filter((m) => m.conta_corrente_id === contaCorrenteId || m.conta_corrente_id == null)
    : meios;

  // Separação por status
  const ativos = meiosFiltrados.filter((m) => m.is_active);
  const inativos = includeInactive ? meiosFiltrados.filter((m) => !m.is_active) : [];

  const isEmpty = !isLoading && ativos.length === 0 && inativos.length === 0;

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled || isLoading}>
      <SelectTrigger className={className}>
        <SelectValue placeholder={isLoading ? 'Carregando...' : placeholder} />
      </SelectTrigger>
      <SelectContent>
        {/* Item "Todos" (opcional — uso em filtros) */}
        {allOptionLabel && (
          <SelectItem value={allOptionValue}>{allOptionLabel}</SelectItem>
        )}

        {isEmpty && (
          <div className="px-2 py-3 text-xs text-muted-foreground text-center">
            Nenhum meio de pagamento cadastrado
          </div>
        )}

        {ativos.length > 0 && (
          <SelectGroup>
            {inativos.length > 0 && (
              <SelectLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Ativos
              </SelectLabel>
            )}
            {ativos.map((m) => {
              const Icon = PaymentMethodIcon[m.tipo_pagamento] || DefaultPaymentIcon;
              return (
                <SelectItem key={m.id} value={m.id}>
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <span>{m.nome}</span>
                  </div>
                </SelectItem>
              );
            })}
          </SelectGroup>
        )}

        {inativos.length > 0 && (
          <SelectGroup>
            <SelectLabel className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
              Inativos
            </SelectLabel>
            {inativos.map((m) => {
              const Icon = PaymentMethodIcon[m.tipo_pagamento] || DefaultPaymentIcon;
              return (
                <SelectItem key={m.id} value={m.id} className="opacity-60">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <span>{m.nome}</span>
                    <span className={cn(
                      'ml-1 rounded-sm border border-border px-1 py-px text-[9px] uppercase tracking-wide text-muted-foreground',
                    )}>
                      Inativo
                    </span>
                  </div>
                </SelectItem>
              );
            })}
          </SelectGroup>
        )}
      </SelectContent>
    </Select>
  );
}
